import { useEffect, useState } from 'react';
import { guests, Guest, GuestList, GuestRequest } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg, guestInviteNotice } from '../utils';

const statusColor: Record<string, string> = {
  active: 'bg-green-500/20 text-green-300',
  expired: 'bg-edge text-gray-400',
  revoked: 'bg-coral/20 text-coral',
  pending: 'bg-yellow-500/20 text-yellow-300',
  approved: 'bg-green-500/20 text-green-300',
  rejected: 'bg-coral/20 text-coral',
  withdrawn: 'bg-edge text-gray-400',
};

function fmt(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : '—';
}

/** Verbleibende Tage bis zum Ablauf (negativ = abgelaufen). */
export function daysLeft(expiresAt: string, now: Date = new Date()): number {
  return Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 86_400_000);
}

export default function GuestsPage() {
  const { me, hasRole } = useAuth();
  const canManage = me?.capability === 'tenant-admin' || hasRole('iga-admin');
  // Sponsorinnen (Realm-Rolle guest-sponsor, ohne IGA-Rolle) beantragen nur;
  // sie sehen ihre eigenen Antraege und Gaeste.
  const sponsorOnly = me?.capability === 'guest-sponsor';

  const [data, setData] = useState<GuestList>({ items: [], maxDays: 90, group: 'guests' });
  const [requests, setRequests] = useState<GuestRequest[]>([]);
  const [showEnded, setShowEnded] = useState(false);
  const [showDecided, setShowDecided] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Einladungsformular
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [purpose, setPurpose] = useState('');
  const [days, setDays] = useState(30);

  const load = async () => {
    setError('');
    try {
      const [g, r] = await Promise.all([guests.list(), guests.requests.list()]);
      setData(g.data);
      setRequests(r.data.items);
    } catch (err) {
      setError(errMsg(err));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setError('');
    setNotice('');
    try {
      await fn();
      await load();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  const invite = () =>
    run(async () => {
      const res = await guests.invite({ email, firstName, lastName, organisation, purpose, days });
      setNotice(guestInviteNotice(res.data, fmt(res.data.expires_at)));
      setEmail('');
      setFirstName('');
      setLastName('');
      setOrganisation('');
      setPurpose('');
    });

  const request = () =>
    run(async () => {
      const res = await guests.requests.create({ email, firstName, lastName, organisation, purpose, days });
      setNotice(`Antrag für ${res.data.email} gestellt – ein Tenant-Admin muss ihn genehmigen.`);
      setEmail('');
      setFirstName('');
      setLastName('');
      setOrganisation('');
      setPurpose('');
    });

  const approve = (r: GuestRequest) =>
    run(async () => {
      const raw = window.prompt(
        `Gastzugang für ${r.email} genehmigen – Laufzeit in Tagen (beantragt: ${r.days}, max. ${data.maxDays})`,
        String(r.days),
      );
      if (raw === null) return;
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n) || n <= 0) throw new Error('Bitte eine positive Anzahl Tage angeben.');
      const res = await guests.requests.approve(r.id, n === r.days ? {} : { days: n });
      setNotice(`Antrag genehmigt, Einladung an ${res.data.email} ausgelöst.`);
    });

  const reject = (r: GuestRequest) =>
    run(async () => {
      const reason = window.prompt(`Antrag für ${r.email} ablehnen – Grund (Pflicht)?`, '');
      if (reason === null) return;
      await guests.requests.reject(r.id, reason);
    });

  const withdraw = (r: GuestRequest) =>
    run(async () => {
      if (!window.confirm(`Antrag für ${r.email} zurückziehen?`)) return;
      await guests.requests.withdraw(r.id);
    });

  const extend = (g: Guest) =>
    run(async () => {
      const raw = window.prompt(`Um wie viele Tage ab heute verlängern? (max. ${data.maxDays})`, '30');
      if (raw === null) return;
      const n = parseInt(raw, 10);
      if (!Number.isFinite(n) || n <= 0) throw new Error('Bitte eine positive Anzahl Tage angeben.');
      await guests.extend(g.id, { days: n });
    });

  const revoke = (g: Guest) =>
    run(async () => {
      const reason = window.prompt(`Gastzugang für ${g.email} entziehen — Grund?`, 'Projekt beendet');
      if (reason === null) return;
      await guests.revoke(g.id, reason);
    });

  const rows = data.items.filter((g) => showEnded || g.status === 'active');
  const pendingRequests = requests.filter((r) => r.status === 'pending');
  const requestRows = showDecided ? requests : pendingRequests;
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Gastzugänge (B2B)</h2>
      <p className="text-sm text-gray-400">
        Externe Personen bekommen ein Konto im eigenen Mandanten, liegen in der Gruppe{' '}
        <code>{data.group}</code>, tragen Sponsor und Zweck und laufen nach spätestens {data.maxDays}{' '}
        Tagen automatisch ab. Ablauf und Entzug deaktivieren das Konto in Keycloak; Nextcloud- und
        Matrix-Freigaben bleiben an die Gruppe gebunden.
      </p>
      {error && <p className="text-coral">{error}</p>}
      {notice && <p className="text-green-300">{notice}</p>}

      {sponsorOnly && (
        <p className="text-sm text-gray-400">
          Du bist als Sponsorin bzw. Sponsor angemeldet: Anträge werden von einem Tenant-Admin geprüft;
          nach der Genehmigung geht die Einladung automatisch an den Gast und du bleibst als Sponsor
          eingetragen. Offene Anträge verfallen nach {data.requestTtlDays ?? 14} Tagen.
        </p>
      )}

      {(canManage || sponsorOnly) && (
        <section className="card space-y-3">
          <h3 className="font-medium text-coral">{canManage ? 'Gast einladen' : 'Gastzugang beantragen'}</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">E-Mail (Login)</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="label">Vorname</label>
              <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <label className="label">Nachname</label>
              <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div>
              <label className="label">Organisation</label>
              <input className="input" value={organisation} onChange={(e) => setOrganisation(e.target.value)} />
            </div>
            <div>
              <label className="label">Gültig (Tage, max. {data.maxDays})</label>
              <input
                className="input"
                type="number"
                min={1}
                max={data.maxDays}
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10) || 0)}
              />
            </div>
            <div>
              <label className="label">Zweck (Projekt, Auftrag)</label>
              <input className="input" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
            </div>
          </div>
          <div className="flex gap-2">
            {canManage && (
              <button
                className="btn-primary"
                disabled={!validEmail || purpose.trim().length < 5 || days < 1 || days > data.maxDays}
                onClick={invite}
              >
                Einladung senden
              </button>
            )}
            <button
              className={canManage ? 'btn-ghost' : 'btn-primary'}
              disabled={!validEmail || purpose.trim().length < 5 || days < 1 || days > data.maxDays}
              onClick={request}
              title={canManage ? 'Antrag stellen, den eine andere Admin-Person genehmigt (Vier-Augen-Prinzip)' : undefined}
            >
              {canManage ? 'Nur beantragen (Vier-Augen)' : 'Antrag stellen'}
            </button>
          </div>
        </section>
      )}

      <section className="card overflow-x-auto">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium text-coral">
            Anträge{pendingRequests.length > 0 ? ` (${pendingRequests.length} offen)` : ''}
          </h3>
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input type="checkbox" checked={showDecided} onChange={(e) => setShowDecided(e.target.checked)} />
            entschiedene anzeigen
          </label>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="p-2">Gast</th>
              <th className="p-2">Organisation</th>
              <th className="p-2">Zweck</th>
              <th className="p-2">Antragsteller</th>
              <th className="p-2">Laufzeit</th>
              <th className="p-2">Status</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {requestRows.length === 0 && (
              <tr>
                <td className="p-2 text-gray-500" colSpan={7}>
                  Keine Anträge.
                </td>
              </tr>
            )}
            {requestRows.map((r) => {
              const own = r.requester_sub === me?.sub;
              return (
                <tr key={r.id} className="border-t border-edge">
                  <td className="p-2">
                    <div className="text-white">{`${r.first_name} ${r.last_name}`.trim() || r.email}</div>
                    <div className="text-xs text-gray-500">{r.email}</div>
                    {r.last_error && r.status === 'pending' && (
                      <div className="text-xs text-yellow-300" title={r.last_error}>
                        letzte Genehmigung fehlgeschlagen
                      </div>
                    )}
                  </td>
                  <td className="p-2">{r.organisation || '—'}</td>
                  <td className="p-2 max-w-xs truncate" title={r.purpose}>
                    {r.purpose}
                  </td>
                  <td className="p-2">
                    {r.requester_email || r.requester_sub}
                    <div className="text-xs text-gray-500">{fmt(r.created_at)}</div>
                  </td>
                  <td className="p-2">{r.days} Tage</td>
                  <td className="p-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${statusColor[r.status] ?? ''}`}>{r.status}</span>
                    {r.decision_reason && <div className="text-xs text-gray-500">{r.decision_reason}</div>}
                  </td>
                  <td className="p-2">
                    {r.status === 'pending' && (
                      <div className="flex gap-2">
                        {canManage && !own && (
                          <>
                            <button className="btn-primary" onClick={() => approve(r)}>
                              Genehmigen
                            </button>
                            <button className="btn-ghost" onClick={() => reject(r)}>
                              Ablehnen
                            </button>
                          </>
                        )}
                        {canManage && own && <span className="text-xs text-gray-500">eigener Antrag</span>}
                        {own && (
                          <button className="btn-ghost" onClick={() => withdraw(r)}>
                            Zurückziehen
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="card overflow-x-auto">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-medium text-coral">{sponsorOnly ? 'Meine Gäste' : 'Gäste'}</h3>
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input type="checkbox" checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)} />
            abgelaufene und entzogene anzeigen
          </label>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="p-2">Gast</th>
              <th className="p-2">Organisation</th>
              <th className="p-2">Zweck</th>
              <th className="p-2">Sponsor</th>
              <th className="p-2">Läuft ab</th>
              <th className="p-2">Status</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td className="p-2 text-gray-500" colSpan={7}>
                  Keine Gastzugänge.
                </td>
              </tr>
            )}
            {rows.map((g) => {
              const left = daysLeft(g.expires_at);
              return (
                <tr key={g.id} className="border-t border-edge">
                  <td className="p-2">
                    <div className="text-white">{g.display_name || g.email}</div>
                    <div className="text-xs text-gray-500">{g.email}</div>
                    {!g.invite_sent && g.status === 'active' && (
                      <div className="text-xs text-yellow-300" title={g.last_error ?? undefined}>
                        {g.last_error?.startsWith('Keine Einladungs-Mail')
                          ? 'Keine Einladungs-Mail (vorhandenes Konto)'
                          : 'Einladungs-Mail nicht zugestellt'}
                      </div>
                    )}
                  </td>
                  <td className="p-2">{g.organisation || '—'}</td>
                  <td className="p-2 max-w-xs truncate" title={g.purpose}>
                    {g.purpose}
                  </td>
                  <td className="p-2">{g.sponsor_email || g.sponsor_sub}</td>
                  <td className="p-2">
                    {fmt(g.expires_at)}
                    {g.status === 'active' && (
                      <div className={`text-xs ${left <= 7 ? 'text-yellow-300' : 'text-gray-500'}`}>
                        {left <= 0 ? 'läuft heute ab' : `noch ${left} Tage`}
                        {g.extend_count > 0 ? ` · ${g.extend_count}× verlängert` : ''}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${statusColor[g.status] ?? ''}`}>{g.status}</span>
                    {g.end_reason && <div className="text-xs text-gray-500">{g.end_reason}</div>}
                  </td>
                  <td className="p-2">
                    {canManage && g.status === 'active' && (
                      <div className="flex gap-2">
                        <button className="btn-ghost" onClick={() => extend(g)}>
                          Verlängern
                        </button>
                        <button className="btn-ghost" onClick={() => revoke(g)}>
                          Entziehen
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}
